// src/pages/VerificationPage.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next"; // i18next hook
import PageWrapper from "../pageWrapper";

// CSS
import "./VerificationPage.css";

// Components
import PageLoader from "../../Components/Other Components/pageLoader";
import Button from "../../Components/Other Components/button";
import Title from "../../Components/Other Components/Title";
import InputField from "../../Components/Other Components/inputField";

// Functions and hooks
import {
  SendPasswordResetEmail,
  CheckIfLoggedIn,
} from "../../BackendAccess/UserHooks";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";

export default function PasswordResetSentPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const [forgotPassword, setForgotPassword] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  let sentFirstMail = false;

  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state || {};

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const checkIfUserIsLoggedIn = async () => {
    try {
      const [data, err] = await fetchWithTimeout(CheckIfLoggedIn);
      if (err) {
        err.isPageError = true;
        setError(err);
        return false;
      }
      return data;
    } catch {
      setError({ err: t("verification.error.server"), errCode: 500 });
      return false;
    }
  };

  const setupPage = async () => {
    const loggedIn = await checkIfUserIsLoggedIn();
    if (!loggedIn) {
      setForgotPassword(true);
    } else if (!sentFirstMail) {
      onResendClick();
      sentFirstMail = true;
    }
  };

  useEffect(() => {
    setupPage();
  }, []);

  const onResendClick = async () => {
    try {
      showNotification(t("verification.info.sending"), "info");
      const [data, err] = await fetchWithTimeout(() =>
        SendPasswordResetEmail(forgotPassword ? emailInput : email)
      );
      if (err) {
        setError(err);
      } else {
        showNotification(t("verification.success.sent"), "success");
      }
    } catch {
      setError({ err: t("verification.error.server"), errCode: 500 });
    }
  };

  const onLoginClick = () => navigate("/login");

  const FormContent = () => (
    <>
      <Title>{t("verification.title")}</Title>
      {!forgotPassword ? (
        <span className="verification-text">
          {t("verification.message.sentTo", { email })}
        </span>
      ) : (
        <span className="verification-text">
          {t("verification.message.enterEmail")}{" "}
          <InputField
            className="mt-1"
            onChange={setEmailInput}
            placeholder={t("verification.placeholder.email")}
            value={emailInput}
          />
        </span>
      )}

      <span className="verification-text font-bold">
        {t("verification.message.checkInbox")}
      </span>
      <span className="verification-text">
        {t("verification.message.notFound")}
      </span>

      <div className="btn-container mt-3 w-100">
        <Button onClick={onResendClick}>
          {forgotPassword
            ? t("verification.button.send")
            : t("verification.button.resend")}
        </Button>
        <Button onClick={onLoginClick}>{t("verification.button.login")}</Button>
      </div>
    </>
  );

  return (
    <PageWrapper>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="container-bordered verification-container mt-3">
            <FormContent />
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
